// The Terugkeerpad: what a login flow preserves of a Deeplink so the user lands
// back on the page they opened instead of on the app's home. Held in the `r`
// query parameter on the login screen.
//
// `r` is an ORIGIN-RELATIVE path plus query, never an absolute URL. That is the
// whole safety argument: a path cannot name another host, so no allowlist of
// trusted domains is needed and no per-environment config can silently break it.
// See docs/adr/0028-return-path-is-origin-relative.md.
//
// Kept dependency-free on purpose so the standalone vm test can load it without
// ES-import handling. It does use the `URL` global — a bare vm context has
// encodeURIComponent but NOT URL, so the test supplies it via
// vm.createContext({ URL }).

// "/foo?a=1" -> "%2Ffoo%3Fa%3D1". Empty for the app root without a query, so a
// plain visit to "/" does not pollute the login URL with a useless r.
function buildReturnParam(pathname, search) {
  const path = typeof pathname === "string" && pathname ? pathname : "/";
  const query = typeof search === "string" ? search : "";
  const target = `${path}${query}`;

  return target === "/" ? "" : encodeURIComponent(target);
}

// Appends `r` to a login URL, joining with & when it already carries a query.
// The user frontend passes location.search through flat (it feeds token/register
// into the login call), so the naive `?r=` would produce a second question mark.
export function withReturnParam(url, pathname, search) {
  const value = buildReturnParam(pathname, search);
  if (!value) {
    return url;
  }

  return `${url}${url.includes("?") ? "&" : "?"}r=${value}`;
}

// Resolves `r` against the current origin. Returns the normalised path to
// navigate to, or null when the caller should fall back to the app's home.
//
// Validation is delegated to the URL parser rather than hand-written, because
// `r.startsWith("/")` is not enough: "//evil.com/x" and "/\evil.com/x" both
// start with a slash and both leave the origin. Comparing the parsed origin is
// the only check that cannot disagree with the navigation that follows — it
// also rejects "javascript:..." (whose origin parses as "null").
//
// A same-origin ABSOLUTE url is accepted and normalised, so login links from
// before this change keep working as long as they point at our own origin.
export function resolveReturnTarget(r, origin) {
  if (typeof r !== "string" || !r) {
    return null;
  }

  try {
    const base = new URL(origin);
    const target = new URL(r, base);

    if (target.origin !== base.origin) {
      return null;
    }

    return `${target.pathname}${target.search}`;
  } catch (err) {
    // r or origin is not parseable at all — treat as no return path.
    return null;
  }
}
